import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IExpenseDetail, ISettings } from '../../types';
import {
  fetchConfiguration as fetchConfigurationApiCall,
  fetchSettings as fetchSettingsApiCall,
  patchSettings as patchSettingsApiCall,
  fetchExpenseDetail as fetchExpenseDetailApiCall,
} from '../../utils/api';
import type { RootState } from '../store';

export const fetchSettings = createAsyncThunk(
  'settings/fetchStatus',
  async () => {
    const settings: ISettings = await fetchSettingsApiCall();
    const config: ISettings = await fetchConfigurationApiCall();
    const expenseDetails: IExpenseDetail[] = await fetchExpenseDetailApiCall();

    return { ...config, ...settings, expenseDetails };
  }
);

export const patchSettings = createAsyncThunk(
  'settings/patchSettings',
  async (state: { clientName?: string; expensesAlertLimit?: number }) => {
    await patchSettingsApiCall(state);

    const settings: ISettings = await fetchSettingsApiCall();

    return settings;
  }
);

export interface ISettingsState {
  settings: ISettings | null;
  loading?: boolean;
  saving?: boolean;
  failed?: boolean;
}

const initialState: ISettingsState = {
  settings: null,
  loading: false,
  saving: false,
  failed: false,
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setSettings: (state, action: PayloadAction<ISettings | null>) => {
      state.settings = action.payload;
    },
    clearSettings: (state) => {
      state.settings = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
        state.loading = false;
        state.failed = false;
      })

      .addCase(fetchSettings.pending, (state) => {
        state.settings = null;
        state.loading = true;
        state.failed = false;
      })
      .addCase(fetchSettings.rejected, (state) => {
        state.settings = null;
        state.loading = false;
        state.failed = true;
      })
      .addCase(patchSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
        state.saving = false;
        state.failed = false;
      })

      .addCase(patchSettings.pending, (state) => {
        state.saving = true;
        state.failed = false;
      })
      .addCase(patchSettings.rejected, (state) => {
        state.settings = null;
        state.saving = false;
        state.failed = true;
      });
  },
});

export const { setSettings, clearSettings } = settingsSlice.actions;
export const selectSettings = (state: RootState) => state.settings;

export default settingsSlice.reducer;
